import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";
import { AI_TRANSPARENCY_NOTE } from "../lib/about";

const COMMITMENTS = [
    {
        n: "T.01",
        title: "EVIDENCE AND INFORMATION",
        copy: "Information extracted from your documents, calculated results, estimates and assumptions are different things — BuildEase labels them differently and never blends them. Verified, Estimated and Assumed are treated as distinct states throughout the platform.",
    },
    {
        n: "T.02",
        title: "COST TRANSPARENCY",
        copy: "Where material pricing information is displayed, BuildEase shows its source or benchmark type, its date, its unit and its geographic relevance. Local relevance matters — a Durban homeowner should not be shown an unqualified Johannesburg price.",
    },
    {
        n: "T.03",
        title: "UNCERTAINTY AND LIMITATIONS",
        copy: "Missing information, unresolved items and areas requiring verification are surfaced, not smoothed over. What BuildEase can show you depends on document quality and the information available — incomplete information is flagged, never invented.",
    },
    {
        n: "T.04",
        title: "RESPONSIBLE AI",
        copy: AI_TRANSPARENCY_NOTE,
    },
    {
        n: "T.05",
        title: "PROFESSIONAL RESPONSIBILITY",
        copy: "BuildEase supports decision-making with better information. It does not replace qualified architects, engineers, quantity surveyors, contractors or other relevant professionals — and it does not provide professional certification or regulatory approval.",
    },
];

const TrustPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="Trust & Transparency | BuildEase"
            description="How BuildEase handles evidence, cost transparency, uncertainty, responsible AI and professional responsibility — better decisions begin with better information."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <BlueprintGrid />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            TRUST &amp; TRANSPARENCY
                        </p>
                        <h1
                            data-testid="trust-page-headline"
                            className="mt-8 max-w-4xl font-display text-4xl font-semibold leading-[1.08] text-white md:text-6xl"
                        >
                            Better decisions begin with better information
                            <span className="text-accent">.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-mist">
                            Five commitments shape how BuildEase communicates
                            what it knows — and what it doesn't.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="relative py-16 lg:py-24">
                <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="space-y-5"
                    >
                        {COMMITMENTS.map((c) => (
                            <motion.div
                                key={c.n}
                                variants={fadeUp}
                                data-testid={`trust-commitment-${c.n.toLowerCase().replace(".", "-")}`}
                                className="border border-white/12 bg-surface/80 px-7 py-8 md:px-9"
                            >
                                <p className="font-mono text-[13px] tracking-[0.3em] text-accent">
                                    {c.n}
                                </p>
                                <h2 className="mt-4 font-display text-2xl font-semibold text-white">
                                    {c.title}
                                </h2>
                                <p className="mt-4 max-w-3xl text-[16px] leading-relaxed text-mist">
                                    {c.copy}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-white/10 pt-10"
                    >
                        <div className="flex flex-wrap gap-4">
                            <Link
                                to="/privacy"
                                data-testid="trust-privacy-link"
                                className="border border-white/25 px-6 py-3.5 text-[15px] font-medium text-white transition-colors duration-300 hover:border-accent"
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/terms"
                                data-testid="trust-terms-link"
                                className="border border-white/25 px-6 py-3.5 text-[15px] font-medium text-white transition-colors duration-300 hover:border-accent"
                            >
                                Terms &amp; Conditions
                            </Link>
                        </div>
                        <Link
                            to="/join-the-pilot"
                            data-testid="trust-get-started"
                            className="group inline-flex items-center gap-2 bg-accent px-7 py-4 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                        >
                            Get Started
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default TrustPage;
